import type { Column as TableColumn } from "@replo/design-system/components/table/Table";
import type { Product } from "schemas/generated/product";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { useAllowScroll } from "@editor/utils/hooks/useAllowScroll";
import { routes } from "@editor/utils/router";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Menu } from "@replo/design-system/components/menu/Menu";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import PageHeader from "@replo/design-system/components/PageHeader";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Table from "@replo/design-system/components/table/Table";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import startCase from "lodash-es/startCase";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ExternalLink,
  Grid2x2,
  List,
  MoreHorizontal,
  Package,
  Search,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { generatePath, useNavigate } from "react-router-dom";

import {
  getPriceRange,
  getStatusBadgeClasses,
  getStatusIcon,
  openShopifyProductPageUrl,
} from "./utils";

const PAGE_SIZE = 50;

type ViewMode = "gallery" | "list";
type StatusFilter = "all" | "active" | "draft" | "archived";
type SortBy = "title" | "status";
type SortOrder = "asc" | "desc";

const STATUS_OPTIONS = [
  { value: "all" as const, label: "Status" },
  { value: "active" as const, label: "Active" },
  { value: "draft" as const, label: "Draft" },
  { value: "archived" as const, label: "Archived" },
];

const sortByOptions = [
  { label: "Alphabetical", value: "title" },
  { label: "Status", value: "status" },
];

const sortOrderOptions = [
  {
    label: "A to Z",
    value: "asc",
    startEnhancer: <ArrowUp size={14} />,
  },
  {
    label: "Z to A",
    value: "desc",
    startEnhancer: <ArrowDown size={14} />,
  },
];

const ProductLoadingSkeleton: React.FC<{ viewMode: ViewMode }> = ({
  viewMode,
}) => (
  <div className="animate-pulse">
    {viewMode === "gallery" ? (
      <div className="grid grid-cols-[repeat(auto-fill,270px)] gap-6 justify-items-start p-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="flex flex-col overflow-hidden">
            <Skeleton className="aspect-square w-full" />
            <div className="flex flex-grow flex-col p-3">
              <Skeleton className="h-4 mb-1 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="p-6">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 py-3 border-b border-border"
          >
            <Skeleton className="h-10 w-10 rounded" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    )}
  </div>
);

const SortMenu: React.FC<{
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  sortOrder: SortOrder;
  setSortOrder: (sortOrder: SortOrder) => void;
}> = ({ sortBy, setSortBy, sortOrder, setSortOrder }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedSortByOption = sortByOptions.find(
    (option) => option.value === sortBy,
  );
  const selectedSortOrderOption = sortOrderOptions.find(
    (option) => option.value === sortOrder,
  );

  return (
    <Popover
      side="bottom"
      shouldPreventDefaultOnInteractOutside={false}
      hideCloseButton
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      align="end"
      triggerAsChild
      content={
        <div className="text-default flex w-full flex-col gap-2 text-sm">
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortBy === option.value}
                onClick={() => setSortBy(option.value as SortBy)}
                size="sm"
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort Order</h3>
            {sortOrderOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortOrder === option.value}
                onClick={() => setSortOrder(option.value as SortOrder)}
                size="sm"
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
        </div>
      }
    >
      <Button
        variant="secondary"
        size="base"
        endEnhancer={<ChevronDown size={14} />}
        startEnhancer={selectedSortOrderOption?.startEnhancer}
      >
        {selectedSortByOption?.label}
      </Button>
    </Popover>
  );
};

const ProductCard: React.FC<{
  product: Product;
  onClick: () => void;
}> = ({ product, onClick }) => {
  return (
    <div
      className="group flex cursor-pointer flex-col overflow-hidden border border-border rounded-lg hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <div className="relative aspect-square overflow-hidden">
        {product.featuredMedia?.url ? (
          <img
            src={product.featuredMedia.url}
            alt={product.title}
            className="h-full w-full object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
          />
        ) : (
          <div className="bg-component text-muted flex h-full w-full items-center justify-center">
            <Package className="h-12 w-12" />
          </div>
        )}
      </div>
      <div className="flex flex-grow flex-col p-3">
        <div className="flex items-center gap-2 mb-0.5">
          {getStatusIcon(product.status)}
          <div className="text-foreground-default truncate text-sm font-medium">
            {product.title}
          </div>
        </div>
        <p className="text-muted flex-grow truncate text-xs ml-4">
          {product.slug ?? "N/A"}
        </p>
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <div className="text-muted text-xs">
              {startCase(product.status)}
            </div>
            <div className="text-muted text-xs">
              {getPriceRange(product.variants)}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-muted text-xs">
              {product.vendor ?? "No vendor"}
            </div>
            <div className="text-muted text-xs">
              {product.variants?.length ?? 0}{" "}
              {(product.variants?.length ?? 0) > 1 ? "variants" : "variant"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductMenu: React.FC<{ onEditProduct: () => void }> = ({
  onEditProduct,
}) => {
  return (
    <Menu
      items={[
        {
          id: "edit",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: "Edit Product",
          onSelect: onEditProduct,
        },
      ]}
      align="end"
      trigger={
        <IconButton
          variant="tertiary"
          size="sm"
          icon={<MoreHorizontal size={16} className="text-muted" />}
        />
      }
    />
  );
};

export function ProductsDashboard() {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [viewMode, setViewMode] = React.useState<ViewMode>("list");
  const [sortBy, setSortBy] = React.useState<SortBy>("title");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("asc");
  const currentProjectId = useCurrentProjectId();
  const navigate = useNavigate();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingProducts,
    isFetching,
  } = trpc.product.list.useInfiniteQuery(
    currentProjectId
      ? {
          projectId: currentProjectId,
          writeThroughProductsSync: isFeatureEnabled(
            "write-through-products-sync",
          ),
          filters: {
            searchTerm: searchQuery,
            status: statusFilter === "all" ? undefined : statusFilter,
          },
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  const allProducts = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.products) ?? [];
  }, [data]);

  const fetchMore = React.useCallback(() => {
    if (!isFetching) {
      void fetchNextPage();
    }
  }, [fetchNextPage, isFetching]);

  useAllowScroll();

  const onProductClick = React.useCallback(
    (clickedProductId: string) => {
      if (currentProjectId) {
        const path = generatePath(routes.shops.products.edit, {
          projectId: currentProjectId,
          productId: clickedProductId,
        });
        navigate(path);
      }
    },
    [currentProjectId, navigate],
  );

  const { project } = useCurrentProjectContext();
  const shopifyStore = project?.integrations?.shopify?.store;

  const onEditInShopify = React.useCallback(() => {
    if (shopifyStore?.shopifyUrl) {
      openShopifyProductPageUrl(shopifyStore.shopifyUrl);
    }
  }, [shopifyStore?.shopifyUrl]);

  const sortedProducts = React.useMemo(() => {
    const products = [...allProducts];
    return products.sort((a, b) => {
      let comparison = 0;

      if (sortBy === "title") {
        comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
      } else if (sortBy === "status") {
        comparison = a.status.localeCompare(b.status);
      }

      return sortOrder === "asc" ? comparison : comparison * -1;
    });
  }, [allProducts, sortBy, sortOrder]);

  const renderMainContent = () => {
    if (!sortedProducts.length && !isLoadingProducts) {
      return (
        <EmptyState
          icon={<Package size={48} />}
          title="No products found"
          description={
            searchQuery || statusFilter !== "all"
              ? "Try adjusting your search or filters."
              : "No products available."
          }
        >
          <Button
            variant="secondary"
            size="sm"
            endEnhancer={<ExternalLink size={14} />}
            onClick={onEditInShopify}
          >
            Edit in Shopify
          </Button>
        </EmptyState>
      );
    }

    if (isLoadingProducts && allProducts.length === 0) {
      return <ProductLoadingSkeleton viewMode={viewMode} />;
    }

    return (
      <InfiniteScroll
        dataLength={allProducts.length}
        next={fetchMore}
        hasMore={hasNextPage}
        loader={
          <div className="flex justify-center py-6">
            <ProductLoadingSkeleton viewMode={viewMode} />
          </div>
        }
        scrollableTarget="window"
      >
        {viewMode === "gallery" ? (
          <div className="grid grid-cols-[repeat(auto-fill,270px)] gap-6 justify-items-start">
            {sortedProducts.map((product: Product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => onProductClick(product.id)}
              />
            ))}
          </div>
        ) : (
          <Table
            data={sortedProducts.map((product) => ({
              ...product,
              key: product.id,
            }))}
            columns={[
              ...productTableColumns,
              {
                key: "actions",
                header: null,
                align: "right" as const,
                renderCell: (product) => (
                  <ProductMenu
                    onEditProduct={() => onProductClick(product.id)}
                  />
                ),
              },
            ]}
            isLoading={false}
            onRowClick={(product) => onProductClick(product.id)}
          />
        )}
      </InfiniteScroll>
    );
  };

  const productTableColumns: TableColumn<Product & { key: string }>[] = [
    {
      key: "product",
      header: "Product",
      renderCell: (product) => (
        <div className="flex items-center gap-3 min-w-0">
          {product.featuredMedia?.url ? (
            <img
              src={product.featuredMedia.url}
              alt={product.title}
              className="h-12 w-12 rounded object-cover flex-shrink-0"
            />
          ) : (
            <div className="bg-component text-muted flex h-12 w-12 items-center justify-center rounded flex-shrink-0">
              <Package className="h-6 w-6" />
            </div>
          )}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {getStatusIcon(product.status)}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="text-default cursor-pointer text-lg font-medium truncate">
                {product.title}
              </div>
              <div className="text-muted text-sm truncate">
                {product.slug ?? "N/A"}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "price",
      header: "Price",
      renderCell: (product) => (
        <div className="text-muted text-sm">
          {getPriceRange(product.variants)}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      renderCell: (product) => (
        <div
          className={twMerge(
            "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
            getStatusBadgeClasses(product.status),
          )}
        >
          {startCase(product.status)}
        </div>
      ),
    },
    {
      key: "vendor",
      header: "Vendor",
      renderCell: (product) => (
        <div className="text-muted text-sm">{product.vendor ?? "N/A"}</div>
      ),
    },
    {
      key: "variants",
      header: "Variants",
      renderCell: (product) => {
        const variantsList =
          product.variants
            ?.slice(0, 5)
            .map((variant) => variant.title ?? "Untitled variant")
            .join(", ") ?? "";
        const hasMore = (product.variants?.length ?? 0) > 5;
        const tooltipContent = hasMore
          ? `${variantsList}... and ${(product.variants?.length ?? 0) - 5} more`
          : variantsList;

        return (
          <Tooltip content={tooltipContent} triggerAsChild side="top">
            <span className="text-muted text-sm cursor-pointer hover:text-foreground">
              {product.variants?.length ?? 0}{" "}
              {product.variants?.length > 1 ? "variants" : "variant"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: "options",
      header: "Options",
      renderCell: (product) => {
        const optionCount = product.options?.length ?? 0;
        if (optionCount === 0) {
          return (
            <Tooltip content="No options" triggerAsChild side="top">
              <span className="text-muted text-sm cursor-pointer hover:text-foreground">
                0 options
              </span>
            </Tooltip>
          );
        }

        const optionsList =
          product.options
            ?.map(
              (option) =>
                `${option.title}: ${option.optionValues?.map((ov) => ov.value).join(", ") ?? "No values"}`,
            )
            .join(", ") ?? "";

        return (
          <Tooltip content={optionsList} triggerAsChild side="top">
            <span className="text-muted text-sm cursor-pointer hover:text-foreground">
              {optionCount} {optionCount > 1 ? "options" : "option"}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const header = (
    <PageHeader
      title="Products"
      description={`${allProducts.length} ${allProducts.length > 1 ? "products" : "product"} synced from Shopify`}
      actions={
        <Button
          variant="secondary"
          size="base"
          endEnhancer={<ExternalLink size={14} />}
          onClick={onEditInShopify}
        >
          Edit in Shopify
        </Button>
      }
    />
  );

  if (isLoadingProducts) {
    return (
      <>
        {header}
        <div className="flex w-full flex-col gap-y-6 p-6">
          <ProductLoadingSkeleton viewMode={viewMode} />
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="flex w-full flex-col gap-y-6 p-6">
        <div className="flex items-center gap-x-3">
          <div className="flex-1">
            <Input
              placeholder="Search products..."
              startEnhancer={<Search size={14} />}
              layoutClassName="w-full"
              size="base"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-x-3 flex-shrink-0">
            <SortMenu
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
            <Combobox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as StatusFilter);
              }}
              layoutClassName="min-w-[150px]"
              trigger={
                <Combobox.SelectionButton
                  title={
                    STATUS_OPTIONS.find((opt) => opt.value === statusFilter)
                      ?.label ?? "Status"
                  }
                  layoutClassName="w-full h-9"
                  titleAlignment="start"
                />
              }
              inputPlaceholder="Filter status..."
              areOptionsSearchable={false}
              align="start"
            />
            <ToggleGroup
              size="base"
              options={[
                {
                  label: <Grid2x2 size={16} />,
                  value: "gallery",
                  tooltipContent: "Gallery View",
                },
                {
                  label: <List size={16} />,
                  value: "list",
                  tooltipContent: "List View",
                },
              ]}
              onChange={(value) => setViewMode(value as ViewMode)}
              selectedValue={viewMode}
            />
          </div>
        </div>
        {renderMainContent()}
      </div>
    </>
  );
}
